/**
 * ActivityTab Component
 * Displays activity timeline (all events in chronological order) from Application Insights
 */

import { useState, useEffect } from 'react';
import {
  MessageSquare, ThumbsUp, CheckCircle, AlertTriangle, AlertCircle,
  ExternalLink, RefreshCw, Clock, Filter
} from 'react-feather';
import { DateRange } from '../../types/logs';

interface ActivityTabProps {
  dateRange: DateRange;
}

interface ActivityTimelineEvent {
  id: string;
  timestamp: string;
  eventType: 'chat' | 'feedback' | 'satisfaction' | 'escalation' | 'error';
  eventName: string;
  conversationId?: string;
  summary: string;
  details: Record<string, unknown>;
  user?: {
    email?: string;
    name?: string;
  };
  severity?: 'info' | 'warning' | 'error';
}

interface ActivityTimelineResult {
  events: ActivityTimelineEvent[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

type EventTypeFilter = 'all' | 'chat' | 'feedback' | 'satisfaction' | 'escalation' | 'error';

const eventTypeConfig: Record<ActivityTimelineEvent['eventType'], {
  icon: React.ElementType;
  color: string;
  bgColor: string;
  label: string;
}> = {
  chat: { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Chat' },
  feedback: { icon: ThumbsUp, color: 'text-green-600', bgColor: 'bg-green-50', label: 'Feedback' },
  satisfaction: { icon: CheckCircle, color: 'text-purple-600', bgColor: 'bg-purple-50', label: 'Satisfaction' },
  escalation: { icon: ExternalLink, color: 'text-orange-600', bgColor: 'bg-orange-50', label: 'Escalation' },
  error: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', label: 'Error' }
};

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function getRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function ActivityTab({ dateRange }: ActivityTabProps) {
  const [data, setData] = useState<ActivityTimelineResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>('all');

  useEffect(() => {
    fetchTimeline();
  }, [dateRange, eventTypeFilter]);

  async function fetchTimeline() {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.set('startDate', dateRange.startDate.toISOString());
      if (dateRange.endDate) params.set('endDate', dateRange.endDate.toISOString());
      if (eventTypeFilter !== 'all') params.set('eventTypes', eventTypeFilter);

      const response = await fetch(`/api/activity-logs/timeline?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to fetch timeline');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
        <span className="ml-2 text-gray-500">Loading activity timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <AlertCircle className="mx-auto mb-2" size={24} />
        <p>{error}</p>
        <button
          onClick={fetchTimeline}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Event Type:</span>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setEventTypeFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                eventTypeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {(Object.entries(eventTypeConfig) as [EventTypeFilter, typeof eventTypeConfig['chat']][]).map(([type, config]) => (
              <button
                key={type}
                onClick={() => setEventTypeFilter(type)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors flex items-center gap-1 ${
                  eventTypeFilter === type
                    ? `${config.bgColor} ${config.color} ring-2 ring-current`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <config.icon size={14} />
                {config.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      {!data?.events?.length ? (
        <div className="text-center py-12 text-gray-500">
          <Clock className="mx-auto mb-2" size={32} />
          <p>No activity found in the selected time range</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <div className="divide-y divide-gray-100">
            {data.events.map((event) => {
              const config = eventTypeConfig[event.eventType];
              const Icon = config.icon;

              return (
                <div
                  key={event.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${
                    event.severity === 'error' ? 'border-l-4 border-red-500' :
                    event.severity === 'warning' ? 'border-l-4 border-yellow-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${config.bgColor}`}>
                      <Icon className={config.color} size={20} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                          {config.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {getRelativeTime(event.timestamp)}
                        </span>
                        {event.user?.email && (
                          <span className="text-xs text-gray-500">
                            • {event.user.name || event.user.email}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-800 break-words">
                        {event.summary}
                      </p>

                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span title={formatTimestamp(event.timestamp)}>
                          {formatTimestamp(event.timestamp)}
                        </span>
                        {event.conversationId && (
                          <span>Conv: {event.conversationId.substring(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination info */}
      {data && data.totalPages > 1 && (
        <div className="text-center text-sm text-gray-500">
          Page {data.currentPage} of {data.totalPages} ({data.totalCount} total events)
        </div>
      )}
    </div>
  );
}
